import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  BusinessConflict,
  ConflictGuardian,
  ConflictSchool,
  ConflictSource,
  CONFLICT_VALUE_KEY,
  isSchoolConflict,
  isTerminal,
  Resolutions,
} from './conflict.model';

/**
 * The comparison itself — the two candidate records and what can be done with
 * them. Shown inside `app-conflict-resolver`'s dialog, which owns where it
 * appears and what a resolved conflict looks like afterwards.
 *
 * Purely driven by the response: `allowed_sources` decides which controls
 * appear, so a new case needs no change here.
 *   [existing, submitted] -> Use This on both cards
 *   [existing]            -> one button, the other card says why not
 *   [acknowledge]         -> a single Accept action
 *   []                    -> terminal, nothing to press
 *
 * The winning value is never sent: only the chosen `source`.
 */
@Component({
  selector: 'app-conflict-panel',
  templateUrl: './conflict-panel.component.html',
  styleUrls: ['./conflict-panel.component.scss'],
})
export class ConflictPanelComponent {
  @Input() conflict: BusinessConflict;
  @Input() resolutions: Resolutions = {};
  @Input() lang = 'en';

  @Output() choose = new EventEmitter<{ field: string; source: ConflictSource }>();

  /** Which parent fields each card lists — same set the bulk detail panel shows. */
  readonly rows = ['parent_name', 'parent_national_id', 'parent_phone_number', 'parent_email'];

  /** What a guardian already linked to the student is listed by (terminal case). */
  readonly parentRows = ['parent_name', 'parent_national_id', 'parent_phone_number'];

  /** What each terminal case tells the admin to do about it. */
  private readonly terminalHints: { [field: string]: string } = {
    student_parent_limit: 'student-conflicts.parent-limit-hint',
    parent_already_linked: 'student-conflicts.already-linked-hint',
  };

  get terminal(): boolean {
    return isTerminal(this.conflict);
  }

  /**
   * The guardians a terminal conflict is about. `student_parent_limit` names
   * the two who already hold the student; the cases that block on a single
   * guardian carry them as `existing_value` instead.
   */
  get terminalParents(): ConflictGuardian[] {
    if (this.conflict?.parents?.length) {
      return this.conflict.parents;
    }
    const existing = this.conflict?.existing_value as ConflictGuardian;
    return existing?.parent_national_id ? [existing] : [];
  }

  /** Falls back to the limit hint, which is what every terminal case said before. */
  get terminalHintKey(): string {
    return this.terminalHints[this.conflict?.field] || 'student-conflicts.parent-limit-hint';
  }

  get isSchool(): boolean {
    return isSchoolConflict(this.conflict);
  }

  get acknowledgeOnly(): boolean {
    return this.conflict?.allowed_sources?.length === 1
      && this.conflict.allowed_sources[0] === 'acknowledge';
  }

  /** The row to outline in red — the one the conflict is actually about. */
  get affectedKey(): string {
    return CONFLICT_VALUE_KEY[this.conflict?.field];
  }

  get chosen(): ConflictSource | null {
    return (this.resolutions?.[this.conflict?.field]?.source as ConflictSource) || null;
  }

  allows(source: ConflictSource): boolean {
    return !!this.conflict?.allowed_sources?.includes(source);
  }

  value(side: 'existing' | 'submitted', key: string): string {
    const src: any = side === 'existing' ? this.conflict?.existing_value : this.conflict?.submitted_value;
    return src?.[key] || '-';
  }

  schoolName(side: 'existing' | 'submitted'): string {
    const src = (side === 'existing' ? this.conflict?.existing_value : this.conflict?.submitted_value) as ConflictSchool;
    return src?.name || '-';
  }

  /**
   * Keeping the submitted national ID rewrites the guardian's ID and
   * regenerates both QR codes — the school should be told before they pick it.
   */
  get warnsAboutQr(): boolean {
    return this.conflict?.field === 'parent_national_id' && this.allows('submitted');
  }

  pick(source: ConflictSource): void {
    if (!this.allows(source) || this.chosen) {
      return;
    }
    this.choose.emit({ field: this.conflict.field, source });
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** Phones read left-to-right in every language, Arabic included. */
  isPhone(key: string): boolean {
    return key === 'parent_phone_number';
  }

  /** The phone arrives under either key depending on the case that reported it. */
  guardianValue(p: ConflictGuardian, key: string): string {
    const value = key === 'parent_phone_number'
      ? p?.parent_phone_number || p?.parent_phone
      : (p as any)?.[key];
    return value || '-';
  }
}
